import { Dialog, DialogContent, DialogTitle, IconButton, Grid, TextField, Button, Typography, Box, Divider, Snackbar, Alert, Switch, FormControlLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useState, useEffect } from 'react';
import { useApi } from '../../hooks/useApi';
import { type UpdateBotPayload, type BotResponseData, UpdateBotPayloadSchema } from '../../types/api';

interface EditarBotModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  bot: BotResponseData | null;
}

export function EditarBotModal({ open, onClose, onSuccess, bot }: EditarBotModalProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const { updateBot } = useApi();

  const [botData, setBotData] = useState<Omit<UpdateBotPayload, 'id'>>({
    name: '',
    token: '',
    start: {
      text: 'Bem-vindo ao bot!',
      banner: [],
      keyboards: []
    },
    remarkets: {
      start: '',
      product: '',
      payment: ''
    }
  });

  useEffect(() => {
    if (bot) {
      const banner = bot.start?.banner ? (Array.isArray(bot.start.banner) ? [...bot.start.banner] : []) : [];

      setBotData({
        name: bot.name,
        token: bot.token,
          archived: !!bot.archived,
        start: {
          text: bot.start?.text || '',
          banner,
          keyboards: bot.start?.keyboards || []
        },
        remarkets: bot.remarkets || {
          start: '',
          product: '',
          payment: ''
        }
      });
    }
  }, [bot]);





  const handleInputChange = (field: string, value: string) => {
    setBotData(prev => ({ ...prev, [field]: value }));
  };

  const handleStartInputChange = (field: string, value: string, bannerIndex?: number) => {
    setBotData(prev => {
      if (field === 'banner' && typeof bannerIndex === 'number') {
        const currentBanner = Array.isArray(prev.start?.banner) ? [...prev.start.banner] : [];
        while (currentBanner.length <= bannerIndex) currentBanner.push('');
        currentBanner[bannerIndex] = value;
        return { ...prev, start: { ...prev.start!, banner: currentBanner } };
      }
      return { ...prev, start: { ...prev.start!, [field]: value } };
    });
  };



  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!bot?._id) {
      setError("ID do bot não disponível");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const updatedBotData = {
        ...botData,
        start: botData.start ? {
          ...botData.start,
          banner: Array.isArray(botData.start.banner) ? botData.start.banner.filter(Boolean) : []
        } : undefined
      };

      const updatePayload: UpdateBotPayload = {
        ...updatedBotData,
        id: bot._id
      };

      try {
        UpdateBotPayloadSchema.parse(updatePayload);
      } catch (validationError) {
        if (validationError instanceof Error) {
          setError(validationError.message || 'Erro de validação nos dados');
        } else {
          setError('Erro de validação nos dados');
        }
        setLoading(false);
        return;
      }

      const response = await updateBot(updatePayload);
      console.log('Bot atualizado com sucesso:', response);
      setSuccess(true);
      if (onSuccess) onSuccess();

      setTimeout(() => onClose(), 1000);
    } catch (err) {
      console.error('Erro ao atualizar bot:', err);
      setError('Ocorreu um erro ao atualizar o bot. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSnackbar = () => {
    setError(null);
    setSuccess(false);
  };

  // --- Estilo Old Money Azul sofisticado + preto ---

  const colors = {
  background: '#000000',       // preto puro
  paper: '#121212',            // preto quase carvão
  primary: '#3b5998',          // azul acinzentado suave e elegante
  primaryLight: '#9aa8c3',  // azul claro suave para hover
  textPrimary: '#E0E0E0',      // cinza claro confortável para texto
  border: '#2E3A4A',           // azul muito escuro apagado para bordas
  error: '#B23A48',
  success: '#3A6A42'
};


  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: colors.paper,
          boxShadow: `0 0 15px ${colors.primary}80`,
          color: colors.textPrimary,
          minWidth: 600,
        }
      }}
    >
      <DialogTitle
        sx={{
          p: 3,
          pb: 0,
          display: 'flex',
          alignItems: 'center',
          color: colors.primary,
          fontWeight: 700,
          fontSize: 24,
          borderBottom: `1px solid ${colors.border}`
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            mr: 2,
            color: colors.primary,
            '&:hover': { color: colors.primaryLight }
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography variant="h5" fontWeight={700}>
          Editar Bot
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Box component="form" autoComplete="off" onSubmit={handleSubmit} sx={{ color: colors.textPrimary }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome"
                placeholder="Nome do bot"
                fullWidth
                margin="normal"
                value={botData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
                InputLabelProps={{ sx: { color: colors.primary } }}
                sx={{
                  input: { color: colors.textPrimary },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: colors.primaryLight },
                    '&.Mui-focused fieldset': { borderColor: colors.primary }
                  }
                }}
              />
              <TextField
                label="Token"
                placeholder="123456:ABC-DEF1234ghIkl-zyx57W2v1u123ew11"
                fullWidth
                margin="normal"
                value={botData.token}
                onChange={(e) => handleInputChange('token', e.target.value)}
                required
                InputLabelProps={{ sx: { color: colors.primary } }}
                sx={{
                  input: { color: colors.textPrimary },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: colors.primaryLight },
                    '&.Mui-focused fieldset': { borderColor: colors.primary }
                  }
                }}
              />

              <FormControlLabel
                control={<Switch checked={!!botData.archived} onChange={(e) => setBotData(prev => ({ ...prev, archived: e.target.checked }))} color="primary" />}
                label="Arquivado"
                sx={{ color: colors.textPrimary, mt: 1 }}
              />

              <Divider sx={{ my: 2, borderColor: colors.border }} />

              <Typography fontWeight={600} mb={1} mt={2} sx={{ color: colors.primary }}>
                Mensagem de Boas-vindas
              </Typography>
              <TextField
                label="Texto"
                placeholder="Bem-vindo ao bot!"
                fullWidth
                margin="normal"
                multiline
                rows={4}
                value={botData.start?.text}
                onChange={(e) => handleStartInputChange('text', e.target.value)}
                InputLabelProps={{ sx: { color: colors.primary } }}
                sx={{
                  input: { color: colors.textPrimary },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': { borderColor: colors.border },
                    '&:hover fieldset': { borderColor: colors.primaryLight },
                    '&.Mui-focused fieldset': { borderColor: colors.primary }
                  }
                }}
              />

              <Divider sx={{ my: 2, borderColor: colors.border }} />
              <Typography fontWeight={600} mb={1} mt={2} sx={{ color: colors.primary }}>
                Banners
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setBotData(prev => ({
                    ...prev,
                    start: {
                      ...prev.start!,
                      banner: [...(prev.start?.banner || []), '']
                    }
                  }));
                }}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  fontWeight: 600,
                  mt: 1,
                  mb: 2,
                  backgroundColor: colors.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: colors.primaryLight
                  }
                }}
              >
                Adicionar Banner
              </Button>
              {botData.start?.banner?.map((banner, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    label={`Banner ${index + 1}`}
                    value={banner}
                    onChange={(e) => {
                      const newBanners = [...(botData.start?.banner || [])];
                      newBanners[index] = e.target.value;
                      setBotData(prev => ({
                        ...prev,
                        start: {
                          ...prev.start!,
                          banner: newBanners
                        }
                      }));
                    }}
                    sx={{
                      flexGrow: 1,
                      input: { color: colors.textPrimary },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: colors.border },
                        '&:hover fieldset': { borderColor: colors.primaryLight },
                        '&.Mui-focused fieldset': { borderColor: colors.primary }
                      }
                    }}
                  />
                  <Button
                    onClick={() => {
                      setBotData(prev => ({
                        ...prev,
                        start: {
                          ...prev.start!,
                          banner: prev.start?.banner?.filter((_, i) => i !== index)
                        }
                      }));
                    }}
                    size="small"
                    color="error"
                  >
                    Remover
                  </Button>
                </Box>
              ))}

              <Divider sx={{ my: 2, borderColor: colors.border }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography fontWeight={600} mb={1} mt={2} sx={{ color: colors.primary }}>
                Callbacks
              </Typography>
              <Button
                variant="contained"
                onClick={() => {
                  setBotData(prev => ({
                    ...prev,
                    start: {
                      ...prev.start!,
                      keyboards: [...(prev.start?.keyboards || []), { name: '', text: '' }]
                    }
                  }));
                }}
                sx={{
                  width: '100%',
                  borderRadius: 2,
                  fontWeight: 600,
                  mt: 1,
                  mb: 2,
                  backgroundColor: colors.primary,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: colors.primaryLight
                  }
                }}
              >
                Adicionar Callback
              </Button>

              {/* Lista de Callbacks */}
              {botData.start?.keyboards?.map((keyboard, index) => (
                <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                  <TextField
                    label={`Nome Callback ${index + 1}`}
                    value={keyboard.name}
                    onChange={(e) => {
                      const newKeyboards = [...(botData.start?.keyboards || [])];
                      if (newKeyboards[index]) {
                        newKeyboards[index].name = e.target.value;
                        setBotData(prev => ({
                          ...prev,
                          start: {
                            ...prev.start!,
                            keyboards: newKeyboards
                          }
                        }));
                      }
                    }}
                    sx={{
                      flexGrow: 1,
                      input: { color: colors.textPrimary },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: colors.border },
                        '&:hover fieldset': { borderColor: colors.primaryLight },
                        '&.Mui-focused fieldset': { borderColor: colors.primary }
                      }
                    }}
                  />
                  <TextField
                    label={`Texto Callback ${index + 1}`}
                    value={keyboard.text}
                    onChange={(e) => {
                      const newKeyboards = [...(botData.start?.keyboards || [])];
                      if (newKeyboards[index]) {
                        newKeyboards[index].text = e.target.value;
                        setBotData(prev => ({
                          ...prev,
                          start: {
                            ...prev.start!,
                            keyboards: newKeyboards
                          }
                        }));
                      }
                    }}
                    sx={{
                      flexGrow: 1,
                      input: { color: colors.textPrimary },
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: colors.border },
                        '&:hover fieldset': { borderColor: colors.primaryLight },
                        '&.Mui-focused fieldset': { borderColor: colors.primary }
                      }
                    }}
                  />
                  <Button
                    onClick={() => {
                      setBotData(prev => ({
                        ...prev,
                        start: {
                          ...prev.start!,
                          keyboards: prev.start?.keyboards?.filter((_, i) => i !== index)
                        }
                      }));
                    }}
                    size="small"
                    color="error"
                  >
                    Remover
                  </Button>
                </Box>
              ))}

            </Grid>
          </Grid>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            sx={{
              mt: 4,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 16,
              height: 48,
              bgcolor: colors.primary,
              color: colors.textPrimary,
              '&:hover': {
                bgcolor: colors.primaryLight,
              }
            }}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Salvar Alterações'}
          </Button>
        </Box>
      </DialogContent>

      <Snackbar open={!!error || success} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={error ? "error" : "success"}
          sx={{
            width: '100%',
            bgcolor: error ? colors.error + 'dd' : colors.success + 'dd',
            color: colors.textPrimary,
            fontWeight: 700,
          }}
        >
          {error || "Bot atualizado com sucesso!"}
        </Alert>
      </Snackbar>
    </Dialog>
  );
}
